import { useState, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { certificateService } from '@/api/services/certificateService'
import { getApiError } from '@/api/client'
import { toast } from '@/store/useToastStore'
import { useAuthStore } from '@/store/useAuthStore'
import { useDebounce } from '@/shared/hooks'
import { cn, DUMMY_AVATAR, onAvatarError } from '@/shared/utils'
import {
  Search, Download, Calendar, Mail,
  ChevronLeft, ChevronRight, X, Eye, FileCheck,
  Clock, CheckCircle, XCircle, MessageSquare,
  AlertCircle, Users,
} from 'lucide-react'
import { Modal } from '@/shared/ui/Modal'

// ── Helpers ───────────────────────────────────────────────────────────────────
function fmtDate(v?: string | null) {
  if (!v) return '—'
  try { return new Date(v).toLocaleDateString('en-GB').replace(/\//g, '-') } catch { return v }
}

// ── Stat tile ─────────────────────────────────────────────────────────────────
function StatTile({ label, value, icon, bg, iconBg, textColor }: {
  label: string; value: number | string
  icon: React.ReactNode; bg: string; iconBg: string; textColor: string
}) {
  return (
    <div className={cn('rounded-2xl p-5 flex items-center gap-4 shadow-sm', bg)}>
      <div className={cn('w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0', iconBg)}>
        {icon}
      </div>
      <div>
        <div className="text-[12px] font-bold uppercase tracking-wider text-gray-400">{label}</div>
        <div className={cn('text-3xl font-black mt-0.5', textColor)}>{String(value).padStart(2, '0')}</div>
      </div>
    </div>
  )
}

// ── Status badge ──────────────────────────────────────────────────────────────
function StatusBadge({ status }: { status: string }) {
  const { t } = useTranslation()
  const u = status?.toUpperCase()
  const cfg = {
    PENDING:  { cls: 'bg-brand-500 text-white',  label: t('certificatePage.status.pending')  },
    APPROVED: { cls: 'bg-green-500 text-white',   label: t('certificatePage.status.approved') },
    REJECTED: { cls: 'bg-red-500 text-white',     label: t('certificatePage.status.rejected') },
  }[u] ?? { cls: 'bg-gray-200 text-gray-600', label: status }
  return (
    <span className={cn('px-2.5 py-0.5 rounded-full text-xs font-bold flex-shrink-0', cfg.cls)}>
      {cfg.label}
    </span>
  )
}

// ── Card bg by status ─────────────────────────────────────────────────────────
function cardBg(status: string) {
  const u = status?.toUpperCase()
  if (u === 'APPROVED') return 'bg-green-50/60'
  if (u === 'REJECTED') return 'bg-red-50/60'
  return 'bg-brand-50/40'
}

// ── Certificate card ──────────────────────────────────────────────────────────
function CertificateCard({
  row, onUpdateStatus,
}: {
  row: any; onUpdateStatus: (row: any) => void
}) {
  const { t } = useTranslation()
  const user    = row.user    ?? {}
  const trainer = row.trainer ?? {}
  const txn     = row.txn     ?? {}
  const status  = row.status  ?? 'PENDING'
  const comments = row.comments ?? ''

  const photoUrl = user.photo_url ?? null

  return (
    <div className={cn('rounded-2xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow overflow-hidden', cardBg(status))}>

      {/* ── Header: avatar + name + txn + status ── */}
      <div className="p-4 flex items-start justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <img src={photoUrl || DUMMY_AVATAR} alt={user.name} onError={onAvatarError}
            className="w-11 h-11 rounded-full object-cover border-2 border-white shadow-sm flex-shrink-0 bg-gray-100" />
          <div className="min-w-0">
            <div className="text-sm font-bold text-gray-900 truncate">{user.name ?? '—'}</div>
            <div className="text-[12px] text-gray-400 font-medium">
              {t('certificatePage.card.transactionId', { id: txn?.id ?? row.id ?? '—' })}
            </div>
          </div>
        </div>
        <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
          <StatusBadge status={status} />
          <span className={cn('inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold border',
            row.is_paid
              ? 'bg-green-50 text-green-700 border-green-200'
              : 'bg-amber-50 text-amber-700 border-amber-200')}>
            {row.is_paid
              ? <><CheckCircle size={10} /> {t('certificatePage.card.paid', 'Paid')}</>
              : <><AlertCircle size={10} /> {t('certificatePage.card.unpaid', 'Unpaid')}</>}
          </span>
        </div>
      </div>

      {/* ── Contact row ── */}
      <div className="px-4 pb-2 space-y-1">
        {user.email && (
          <div className="flex items-center gap-1 text-xs text-gray-500 min-w-0">
            <Mail size={10} className="text-gray-400 flex-shrink-0" />
            <span className="truncate">{user.email}</span>
          </div>
        )}
        <div className="flex items-center gap-1 text-xs text-gray-500">
          <Calendar size={10} className="text-gray-400" />
          {t('certificatePage.card.requestedAt', { date: fmtDate(row.request_date ?? row.created_at) })}
        </div>
      </div>

      {/* ── Trainer Info ── */}
      <div className="mx-4 mb-2 rounded-xl border border-gray-200 overflow-hidden bg-white/60">
        <div className="px-3 py-1.5 text-xs font-bold text-gray-700 bg-gray-50/80 border-b border-gray-100">
          {t('certificatePage.card.trainerInfo')}
        </div>
        <div className="px-3 py-2 flex items-center gap-2.5">
          <img src={trainer.photo_url || DUMMY_AVATAR} alt={trainer.name ?? ''} onError={onAvatarError}
            className="w-9 h-9 rounded-full object-cover flex-shrink-0 bg-gray-100" />
          <div className="min-w-0">
            <div className="text-sm font-bold text-gray-800 truncate">{trainer.name ?? '—'}</div>
            {trainer.email && <div className="text-xs text-gray-500 truncate">{trainer.email}</div>}
          </div>
        </div>
      </div>

      {/* ── Trainer's Comment (the important note about the student) ── */}
      {status?.toUpperCase() !== 'REJECTED' && comments && (
        <div className="mx-4 mb-2 rounded-xl bg-brand-50 border border-brand-100 px-3 py-2.5">
          <div className="flex items-center gap-1.5 text-[11px] font-bold text-brand-700 uppercase tracking-wide mb-1">
            <MessageSquare size={11} className="flex-shrink-0" />
            {t('certificatePage.card.trainerComment', "Trainer's Comment")}
          </div>
          <p className="text-xs text-gray-700 leading-relaxed break-words">{comments}</p>
        </div>
      )}

      {/* ── Reject Reason (if rejected + has comments) ── */}
      {status?.toUpperCase() === 'REJECTED' && comments && (
        <div className="mx-4 mb-2 rounded-xl bg-red-50 border border-red-200 px-3 py-2 flex items-center gap-2">
          <AlertCircle size={12} className="text-red-400 flex-shrink-0" />
          <div className="text-xs text-red-600">
            <span className="font-bold">{t('certificatePage.card.rejectReason')}</span>{comments}
          </div>
        </div>
      )}

      {/* ── Inline expand form — only while the request is still pending ── */}
      {status?.toUpperCase() !== 'APPROVED' && status?.toUpperCase() !== 'REJECTED' ? (
        <div className="px-4 pb-4">
          <button
            onClick={() => onUpdateStatus(row)}
            className="w-full py-2 rounded-xl border border-brand-300 text-brand-600 text-xs font-semibold flex items-center justify-center gap-1.5 hover:bg-brand-50 transition-colors"
          >
            <FileCheck size={12} /> {t('certificatePage.card.updateStatus')}
          </button>
        </div>
      ) : (
        <div className="pb-4" />
      )}
    </div>
  )
}

// ── Update Status Modal ───────────────────────────────────────────────────────
function UpdateStatusModal({
  open, onClose, certificate, onSuccess,
}: {
  open: boolean; onClose: () => void; certificate: any | null; onSuccess: () => void
}) {
  const { t } = useTranslation()
  const [status, setStatus]   = useState('PENDING')
  const [note, setNote]       = useState('')
  const [loading, setLoading] = useState(false)
  const [submitError, setSubmitError] = useState('')

  // Reset fields (and clear any prior error) whenever a different cert is opened.
  useEffect(() => {
    if (certificate) {
      setStatus(certificate.status ?? 'PENDING')
      setNote(certificate.comments ?? '')
      setSubmitError('')
    }
  }, [certificate])

  const handleSave = async () => {
    if (!certificate) return
    setSubmitError('')
    setLoading(true)
    try {
      const fd = new FormData()
      fd.append('certificate_request_id', String(certificate.id))
      fd.append('status', status)
      if (note) fd.append('comments', note)
      await certificateService.updateStatus(fd as any)
      toast.success(t('certificatePage.modal.updated', 'Status updated successfully'))
      onSuccess()
      onClose()
    } catch (e) {
      // Surface the backend's actual message (e.g. "Certificate must be paid
      // before approval") instead of silently swallowing it.
      const msg = getApiError(e)
      setSubmitError(msg)
      toast.error(msg)
    } finally {
      setLoading(false)
    }
  }

  const STATUS_OPTS = [
    { value: 'PENDING',  label: t('certificatePage.status.pending'),  icon: <Clock size={14} className="text-brand-500" /> },
    { value: 'APPROVED', label: t('certificatePage.status.approved'), icon: <CheckCircle size={14} className="text-green-500" /> },
    { value: 'REJECTED', label: t('certificatePage.status.rejected'), icon: <XCircle size={14} className="text-red-500" /> },
  ]

  return (
    <Modal open={open} onClose={onClose} title={t('certificatePage.modal.title')} size="sm">
      <div className="space-y-4">
        {/* Info hint */}
        <div className="flex items-center gap-2 p-3 rounded-xl bg-brand-50 border border-brand-100 text-xs text-brand-700">
          <AlertCircle size={13} className="flex-shrink-0" />
          {t('certificatePage.modal.hint')}
        </div>

        {/* Status select */}
        <div>
          <label className="text-xs font-bold text-gray-700 block mb-1.5">{t('certificatePage.modal.status')}</label>
          <select
            value={status}
            onChange={e => setStatus(e.target.value)}
            className="w-full text-sm border border-gray-200 rounded-xl px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-brand-400 bg-white"
          >
            <option value="">{t('certificatePage.modal.selectStatus')}</option>
            {STATUS_OPTS.map(o => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>

        {/* Note */}
        <div>
          <label className="text-xs font-bold text-gray-700 block mb-1.5">{t('certificatePage.modal.note')}</label>
          <textarea
            value={note}
            onChange={e => setNote(e.target.value)}
            rows={3}
            placeholder={t('certificatePage.modal.notePlaceholder')}
            className="w-full text-sm border border-gray-200 rounded-xl px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-brand-400 resize-none"
          />
        </div>

        {submitError && (
          <div className="flex items-start gap-2 p-3 rounded-xl bg-red-50 border border-red-200 text-xs text-red-600 whitespace-pre-line">
            <AlertCircle size={13} className="flex-shrink-0 mt-0.5" />
            <span>{submitError}</span>
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center gap-3 pt-1">
          <button
            onClick={onClose}
            className="flex-1 py-2.5 rounded-xl border border-gray-200 text-sm font-semibold text-gray-600 hover:bg-gray-50 transition-colors"
          >
            {t('certificatePage.modal.cancel')}
          </button>
          <button
            onClick={handleSave}
            disabled={loading}
            className="flex-1 py-2.5 rounded-xl bg-brand-500 hover:bg-brand-600 text-white text-sm font-bold transition-colors disabled:opacity-50"
          >
            {loading ? t('certificatePage.modal.saving') : t('certificatePage.modal.save')}
          </button>
        </div>
      </div>
    </Modal>
  )
}

// ── Paginator ─────────────────────────────────────────────────────────────────
function Paginator({ page, lastPage, total, perPage, onPage, onPerPage }: {
  page: number; lastPage: number; total: number; perPage: number
  onPage: (p: number) => void; onPerPage: (n: number) => void
}) {
  const { t } = useTranslation()
  const pageNums = (() => {
    let s = Math.max(1, page - 2)
    if (s + 4 > lastPage) s = Math.max(1, lastPage - 4)
    return Array.from({ length: Math.min(5, lastPage) }, (_, i) => s + i).filter(p => p <= lastPage)
  })()
  return (
    <div className="bg-white rounded-2xl border border-gray-200 px-5 py-3 flex items-center justify-between gap-4 flex-wrap shadow-sm">
      <div className="flex items-center gap-2 text-xs text-gray-500">
        <span>{t('certificatePage.paginator.itemsPerPage')}</span>
        <select value={perPage} onChange={e => onPerPage(Number(e.target.value))}
          className="border border-gray-200 rounded-lg px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-brand-400">
          {[10, 15, 20, 25, 50].map(n => <option key={n} value={n}>{n}</option>)}
        </select>
        <span>{t('certificatePage.paginator.itemsRange', { from: (page - 1) * perPage + 1, to: Math.min(page * perPage, total), total })}</span>
      </div>
      <div className="flex items-center gap-1">
        <button onClick={() => onPage(Math.max(1, page - 1))} disabled={page <= 1}
          className="w-8 h-8 rounded-lg border border-gray-200 flex items-center justify-center text-gray-500 hover:bg-gray-50 disabled:opacity-40">
          <ChevronLeft size={14} className="rtl:rotate-180" />
        </button>
        {pageNums.map(p => (
          <button key={p} onClick={() => onPage(p)}
            className={cn('w-8 h-8 rounded-lg border text-xs font-semibold',
              p === page ? 'bg-brand-500 text-white border-brand-500' : 'border-gray-200 text-gray-600 hover:bg-gray-50')}>
            {p}
          </button>
        ))}
        <button onClick={() => onPage(Math.min(lastPage, page + 1))} disabled={page >= lastPage}
          className="w-8 h-8 rounded-lg border border-gray-200 flex items-center justify-center text-gray-500 hover:bg-gray-50 disabled:opacity-40">
          <ChevronRight size={14} className="rtl:rotate-180" />
        </button>
        <span className="ms-1 text-xs text-gray-400">{t('certificatePage.paginator.of')} {lastPage}</span>
      </div>
    </div>
  )
}

// ── Skeleton card ─────────────────────────────────────────────────────────────
function SkeletonCard() {
  return (
    <div className="rounded-2xl border border-gray-200 bg-white animate-pulse overflow-hidden">
      <div className="p-4 flex items-center gap-3">
        <div className="w-11 h-11 rounded-full bg-gray-200 flex-shrink-0" />
        <div className="flex-1 space-y-1.5">
          <div className="h-3 bg-gray-200 rounded w-2/3" />
          <div className="h-2 bg-gray-100 rounded w-1/2" />
        </div>
        <div className="h-5 w-16 rounded-full bg-gray-200" />
      </div>
      <div className="px-4 pb-2 space-y-1.5">
        <div className="h-2 bg-gray-100 rounded w-3/4" />
        <div className="h-2 bg-gray-100 rounded w-1/2" />
      </div>
      {[1, 2, 3].map(i => (
        <div key={i} className="mx-4 mb-2 h-14 bg-gray-100 rounded-xl" />
      ))}
      <div className="px-4 pb-4"><div className="h-8 bg-gray-100 rounded-xl" /></div>
    </div>
  )
}

// ══════════════════════════════════════════════════════════════════════════════
// Main Page
// ══════════════════════════════════════════════════════════════════════════════
export function CertificatePage() {
  const { t } = useTranslation()
  const qc = useQueryClient()
  const user = useAuthStore(s => s.user)
  const isSchoolAdmin = String((user as any)?.user_type ?? '').toUpperCase() === 'SCHOOL_ADMIN'
  const fetchCertificates = isSchoolAdmin ? certificateService.list : certificateService.listAdmin

  const [search,    setSearch]    = useState('')
  const [page,      setPage]      = useState(1)
  const [perPage,   setPerPage]   = useState(15)
  const [modalCert, setModalCert] = useState<any | null>(null)

  const debouncedSearch = useDebounce(search)

  // No more status-filtered tabs — every request shows together, and each
  // card's own status (not which tab you're on) decides whether it still gets
  // an "Update Status" action.
  const pageTitle = t('certificatePage.titleAll')

  const { data, isLoading } = useQuery({
    queryKey: ['certificates', page, perPage, debouncedSearch, isSchoolAdmin],
    queryFn: () => fetchCertificates({
      page,
      limit: perPage,
      search: debouncedSearch || undefined,
    }),
    staleTime: 30_000,
  })

  // Fetch all 3 counts for the stat tiles
  const { data: allData } = useQuery({
    queryKey: ['certificates-all-counts', isSchoolAdmin],
    queryFn: async () => {
      const [pending, approved, rejected] = await Promise.all([
        fetchCertificates({ page: 1, limit: 1, status: 'PENDING'  }),
        fetchCertificates({ page: 1, limit: 1, status: 'APPROVED' }),
        fetchCertificates({ page: 1, limit: 1, status: 'REJECTED' }),
      ])
      return {
        pending:  (pending  as any)?.meta?.total ?? (pending  as any)?.total ?? 0,
        approved: (approved as any)?.meta?.total ?? (approved as any)?.total ?? 0,
        rejected: (rejected as any)?.meta?.total ?? (rejected as any)?.total ?? 0,
      }
    },
    staleTime: 60_000,
  })

  // Show the most recently created request first. The API doesn't accept a sort
  // param, so we order the current page client-side (created_at, then id as a
  // fallback since a higher id is always newer).
  const certTime = (r: any) => {
    const ts = new Date(r?.created_at ?? r?.request_date ?? 0).getTime()
    return Number.isNaN(ts) ? 0 : ts
  }
  const rows: any[] = [...((data as any)?.data ?? [])].sort(
    (a, b) => certTime(b) - certTime(a) || (Number(b?.id ?? 0) - Number(a?.id ?? 0))
  )
  const total        = (data as any)?.meta?.total ?? (data as any)?.total ?? 0
  const lastPage     = Math.ceil(total / perPage) || 1

  const totalRequests = (allData?.pending ?? 0) + (allData?.approved ?? 0) + (allData?.rejected ?? 0)

  const handleExport = () => {
    const csv = [
      ['ID', 'Student', 'Trainer', 'City', 'Gear', 'Status', 'Request Date'],
      ...rows.map((r: any) => [
        r.id, r.user?.name ?? '', r.trainer?.name ?? '',
        r.city?.city_translations?.[0]?.name ?? '',
        r.gear ?? '', r.status ?? '', r.request_date ?? r.created_at ?? '',
      ]),
    ].map(r => r.join(',')).join('\n')
    const blob = new Blob([csv], { type: 'text/csv' })
    const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = 'certificates.csv'; a.click()
  }

  const handleStatusUpdated = useCallback(() => {
    qc.invalidateQueries({ queryKey: ['certificates'] })
    qc.invalidateQueries({ queryKey: ['certificates-all-counts'] })
  }, [qc])

  return (
    <div className="space-y-5">

      {/* ── Header ── */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-1.5 text-xs text-gray-400 mb-1">
            <span>{t('certificatePage.breadcrumb.dashboard')}</span><span>/</span>
            <span className="text-brand-500 font-semibold">{t('certificatePage.breadcrumb.certificate')}</span>
          </div>
          <h1 className="text-xl font-bold text-gray-900">{pageTitle}</h1>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          {/* Download */}
          <button onClick={handleExport}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-green-500 hover:bg-green-600 text-white text-xs font-semibold transition-colors">
            <Download size={12} /> {t('certificatePage.download')}
          </button>
          {/* Search */}
          <div className="flex items-center gap-1.5 border border-gray-200 rounded-lg px-3 py-2 bg-white min-w-44">
            <Search size={12} className="text-gray-400 flex-shrink-0" />
            <input
              value={search}
              onChange={e => { setSearch(e.target.value); setPage(1) }}
              placeholder={t('certificatePage.searchPlaceholder')}
              className="text-xs text-gray-600 placeholder-gray-400 focus:outline-none flex-1 bg-transparent min-w-0"
            />
            {search && (
              <button onClick={() => setSearch('')}><X size={11} className="text-gray-300 hover:text-gray-500" /></button>
            )}
          </div>
        </div>
      </div>

      {/* ── Stat tiles ── */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <StatTile
          label={t('certificatePage.stats.totalRequest')} value={totalRequests}
          icon={<FileCheck size={22} className="text-teal-500" />}
          bg="bg-teal-50" iconBg="bg-teal-100" textColor="text-teal-700"
        />
        <StatTile
          label={t('certificatePage.stats.approved')} value={allData?.approved ?? 0}
          icon={<CheckCircle size={22} className="text-brand-500" />}
          bg="bg-brand-50" iconBg="bg-brand-100" textColor="text-brand-700"
        />
        <StatTile
          label={t('certificatePage.stats.pending')} value={allData?.pending ?? 0}
          icon={<Clock size={22} className="text-green-500" />}
          bg="bg-green-50" iconBg="bg-green-100" textColor="text-green-700"
        />
        <StatTile
          label={t('certificatePage.stats.rejected')} value={allData?.rejected ?? 0}
          icon={<XCircle size={22} className="text-red-500" />}
          bg="bg-red-50" iconBg="bg-red-100" textColor="text-red-700"
        />
      </div>

      {/* ── Card grid ── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5">
        {isLoading
          ? Array.from({ length: 6 }).map((_, i) => <SkeletonCard key={i} />)
          : rows.length === 0
            ? (
              <div className="col-span-3 flex flex-col items-center justify-center py-20 text-center">
                <div className="w-14 h-14 rounded-full bg-gray-100 flex items-center justify-center mb-3">
                  <FileCheck size={24} className="text-gray-300" />
                </div>
                <p className="text-sm font-semibold text-gray-500">{t('certificatePage.noCertificatesFound')}</p>
              </div>
            )
            : rows.map((row: any) => (
              <CertificateCard
                key={row.id}
                row={row}
                onUpdateStatus={setModalCert}
              />
            ))
        }
      </div>

      {/* ── Pagination ── */}
      {!isLoading && total > 0 && (
        <Paginator
          page={page} lastPage={lastPage} total={total} perPage={perPage}
          onPage={setPage} onPerPage={n => { setPerPage(n); setPage(1) }}
        />
      )}

      {/* ── Update Status Modal ── */}
      <UpdateStatusModal
        open={!!modalCert}
        onClose={() => setModalCert(null)}
        certificate={modalCert}
        onSuccess={handleStatusUpdated}
      />
    </div>
  )
}
